export interface Attribution {
  source: string;
  channel: string;
  campaign: string;
  content: string;
}

const STORAGE_KEY = "minuta.launch.attribution.v1";
const UNKNOWN = "unknown";

function safeHost(raw: string | null): string {
  if (!raw) return "";
  try {
    return new URL(raw).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function sourceFromReferrer(host: string): string {
  if (!host) return UNKNOWN;
  if (host.includes("producthunt.com")) return "product_hunt";
  if (host.includes("news.ycombinator.com")) return "hacker_news";
  if (host.includes("indiehackers.com")) return "indie_hackers";
  if (host.includes("x.com") || host.includes("twitter.com")) return "x";
  if (host.includes("linkedin.com")) return "linkedin";
  if (host.includes("reddit.com")) return "reddit";
  return "referral";
}

function readStored(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    return {
      source: typeof parsed.source === "string" && parsed.source.trim() ? parsed.source.trim() : UNKNOWN,
      channel: typeof parsed.channel === "string" && parsed.channel.trim() ? parsed.channel.trim() : UNKNOWN,
      campaign: typeof parsed.campaign === "string" && parsed.campaign.trim() ? parsed.campaign.trim() : UNKNOWN,
      content: typeof parsed.content === "string" && parsed.content.trim() ? parsed.content.trim() : UNKNOWN,
    };
  } catch {
    return null;
  }
}

function writeStored(value: Attribution): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures (private mode, blocked storage, etc.).
  }
}

function normalize(raw: string | null): string {
  const trimmed = (raw ?? "").trim();
  return trimmed || UNKNOWN;
}

export function captureAttributionFromLocation(): Attribution {
  if (typeof window === "undefined") {
    return { source: UNKNOWN, channel: UNKNOWN, campaign: UNKNOWN, content: UNKNOWN };
  }

  const url = new URL(window.location.href);
  const ref = url.searchParams.get("ref");
  const utmSource = url.searchParams.get("utm_source");
  const utmMedium = url.searchParams.get("utm_medium");
  const utmCampaign = url.searchParams.get("utm_campaign");
  const utmContent = url.searchParams.get("utm_content");
  const referrerHost = safeHost(document.referrer);
  const hasExplicitAttribution = Boolean(ref || utmSource || utmMedium || utmCampaign || utmContent || referrerHost);
  const prev = readStored();
  if (!hasExplicitAttribution && prev) {
    return prev;
  }

  const source = normalize(utmSource ?? ref ?? sourceFromReferrer(referrerHost));
  const channel = normalize(utmMedium ?? ref ?? source);
  const campaign = normalize(utmCampaign);
  const content = normalize(utmContent);

  const next = { source, channel, campaign, content };
  // Last-touch update: if this page carries explicit campaign tags, overwrite.
  // Otherwise keep existing non-unknown campaign if we already have one.
  if (
    prev &&
    campaign === UNKNOWN &&
    content === UNKNOWN &&
    prev.campaign !== UNKNOWN &&
    source === prev.source &&
    channel === prev.channel
  ) {
    return prev;
  }
  writeStored(next);
  return next;
}

export function getAttribution(): Attribution {
  const stored = readStored();
  if (stored) return stored;
  return captureAttributionFromLocation();
}

export function attributionProps(): Record<string, string> {
  const a = getAttribution();
  return {
    source: a.source,
    channel: a.channel,
    campaign: a.campaign,
    content: a.content,
    utm_source: a.source,
    utm_medium: a.channel,
    utm_campaign: a.campaign,
    utm_content: a.content,
  };
}
